import React, { useState, useCallback } from "react";
import {
  PlusIcon,
  TrashIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  EyeIcon,
  PencilIcon,
  XMarkIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  UserCircleIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import {
  ProjectFormElement,
  WorkflowContainerStatus,
  WorkflowFormValueSuggestion,
  User,
  UserRole,
  Country,
} from "../../types";
import { apiService } from "../../services/apiService";
import toast from "../../utils/toast";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import { getProvinces, getDistricts, getNeighborhoods } from "../../data/turkey-locations";

interface ProjectFormBuilderProps {
  initialData?: any;
  onSave?: (formData: any) => void;
  onSubmit?: (projectData: any) => void;
  onCancel?: () => void;
  isEditMode?: boolean;
  readonly?: boolean;
  compact?: boolean;
  workflowContainerId?: number;
}

// ELEMENT_ID_MAP will be created dynamically with translations

// Helper: Convert Turkish text to URL-safe slug
function slugify(text: string): string {
  const trMap: Record<string, string> = {
    ç: "c",
    ğ: "g",
    ı: "i",
    İ: "i",
    ö: "o",
    ş: "s",
    ü: "u",
    Ç: "c",
    Ğ: "g",
    Ö: "o",
    Ş: "s",
    Ü: "u",
  };

  return text
    .split("")
    .map((char) => trMap[char] || char)
    .join("")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

// AVAILABLE_PROJECT_FORM_ELEMENTS will be created dynamically with translations

export const ProjectFormBuilder: React.FC<ProjectFormBuilderProps> = ({
  initialData,
  onSave,
  onSubmit,
  onCancel,
  isEditMode = false,
  readonly = false,
  compact = false,
  workflowContainerId,
}) => {
  const { t, i18n } = useTranslation([
    "forms",
    "projects",
    "common",
    "workflows",
  ]);
  const { getWorkflowLabel, hasWorkflowTerminologyOverride, isAdmin, isManager } =
    useAuth();
  const [employees, setEmployees] = useState<User[]>([]);
  const [projectManagers, setProjectManagers] = useState<User[]>([]);
  const [projectManagerSearch, setProjectManagerSearch] = useState("");
  const [selectedProjectManagerIds, setSelectedProjectManagerIds] = useState<
    number[]
  >([]);
  const [projectManagerSelectionTouched, setProjectManagerSelectionTouched] =
    useState(false);
  const [projectManagersLoaded, setProjectManagersLoaded] = useState(false);
  const [countries, setCountries] = useState<Country[]>([]);
  const [suggestionsByElement, setSuggestionsByElement] = useState<
    Record<string, WorkflowFormValueSuggestion[]>
  >({});
  const [showSuggestionsModal, setShowSuggestionsModal] = useState(false);
  const [activeSuggestionElement, setActiveSuggestionElement] =
    useState<ProjectFormElement | null>(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);

  const canAssignProjectManagers = isAdmin() || isManager();
  const canViewFinancials = isAdmin() || isManager();
  const FINANCIAL_PROJECT_ELEMENT_TYPES = React.useMemo(
    () =>
      new Set<ProjectFormElement["type"]>([
        "budget_input",
        "currency_input",
        "offer_amount",
        "cost_calculator",
        "warning_threshold",
        "notification_roles",
        "invoice",
      ]),
    []
  );
  const isFinancialProjectElement = useCallback(
    (element?: { type?: ProjectFormElement["type"] } | null) =>
      Boolean(
        element?.type && FINANCIAL_PROJECT_ELEMENT_TYPES.has(element.type)
      ),
    [FINANCIAL_PROJECT_ELEMENT_TYPES]
  );
  const sanitizeSelectedElementsForUser = useCallback(
    (elements: ProjectFormElement[]) =>
      canViewFinancials
        ? elements
        : elements.filter((element) => !isFinancialProjectElement(element)),
    [canViewFinancials, isFinancialProjectElement]
  );
  const sanitizePaletteElementsForUser = useCallback(
    (elements: Omit<ProjectFormElement, "id">[]) =>
      canViewFinancials
        ? elements
        : elements.filter(
            (element) =>
              !isFinancialProjectElement(element as ProjectFormElement)
          ),
    [canViewFinancials, isFinancialProjectElement]
  );

  // Company-specific terminology
  const workflowLabel = getWorkflowLabel();
  const useWorkflowOverride = hasWorkflowTerminologyOverride();
  const isEnglish = i18n.language?.toLowerCase().startsWith("en");
  const workflowNameLabel = useWorkflowOverride
    ? `${workflowLabel} ${isEnglish ? "Name" : "Adı"}`
    : t("forms:projectBuilder.workflowName");

  const countryOptions = React.useMemo(() => {
    if (countries.length === 0) {
      return [t("forms:countries.turkey"), t("forms:countries.germany")];
    }
    const normalize = (value: string) => value.toLocaleLowerCase("tr-TR");
    const mapped = countries.map((country) => {
      if (country.code === "TR") {
        return t("forms:countries.turkey");
      }
      if (country.code === "DE") {
        return t("forms:countries.germany");
      }
      return country.name;
    });
    const uniqueOptions = Array.from(
      new Map(mapped.map((option) => [normalize(option), option])).values(),
    );
    const priority = [t("forms:countries.turkey"), t("forms:countries.germany")];
    const prioritySet = new Set(priority.map(normalize));
    const prioritized = priority.filter((label) =>
      uniqueOptions.some((option) => normalize(option) === normalize(label)),
    );
    const rest = uniqueOptions
      .filter((option) => !prioritySet.has(normalize(option)))
      .sort((a, b) => a.localeCompare(b, "tr", { sensitivity: "base" }));

    return [...prioritized, ...rest];
  }, [countries, t]);

  React.useEffect(() => {
    const loadEmployees = async () => {
      try {
        setProjectManagersLoaded(false);
        const users = await apiService.getUsers();
        setEmployees(users);
      } catch (error) {
      } finally {
        setProjectManagersLoaded(true);
      }
    };
    loadEmployees();
  }, []);

  React.useEffect(() => {
    if (!canAssignProjectManagers) {
      return;
    }
    const candidates = employees
      .filter(
        (user) =>
          user.role === UserRole.EMPLOYEE ||
          user.role === UserRole.PROJECT_MANAGER
      )
      .sort((a, b) => {
        const nameA = `${a.firstName ?? ""} ${a.lastName ?? ""}`.trim();
        const nameB = `${b.firstName ?? ""} ${b.lastName ?? ""}`.trim();
        return nameA.localeCompare(nameB, "tr");
      });
    setProjectManagers(candidates);
  }, [employees, canAssignProjectManagers]);

  React.useEffect(() => {
    if (!canAssignProjectManagers) {
      return;
    }
    const rawIds =
      initialData?.projectManagerIds || initialData?.projectManagers;
    if (Array.isArray(rawIds)) {
      const parsed = rawIds
        .map((id: any) => Number.parseInt(String(id), 10))
        .filter((id: number) => !Number.isNaN(id));
      setSelectedProjectManagerIds(parsed);
    }
  }, [initialData, canAssignProjectManagers]);

  React.useEffect(() => {
    const loadCountries = async () => {
      try {
        const data = await apiService.getCountries();
        setCountries(data || []);
      } catch (error) {
        setCountries([]);
      }
    };
    loadCountries();
  }, []);

  // Create ELEMENT_ID_MAP dynamically with translations
  const ELEMENT_ID_MAP: Record<string, string> = React.useMemo(
    () => ({
      [t("projects:projectName")]: "name",
      [t("projects:clientEmployer")]: "clientName",
      [t("projects:startDate")]: "startDate",
      [t("projects:endDate")]: "endDate",
      [t("projects:projectDurationDays")]: "estimatedDurationHours",
      [t("projects:projectType")]: "projectType",
      [t("projects:budgetAmount")]: "budget",
      [t("projects:progressCalculationMethod")]: "progressCalculationMethod",
      [t("projects:projectDescription")]: "description",
      [t("projects:projectManager")]: "projectManager",
      [t("forms:elementLabels.offerAmountInput")]: "offer_amount",
      Fatura: "invoice",
    }),
    [t]
  );

  // Define editable element types (custom/generic elements)
  const EDITABLE_TYPES = [
    "text",
    "number",
    "date",
    "select",
    "checkbox",
    "employee_select",
    "currency_input",
    "offer_amount",
    "province_select",
    "district_select",
    "neighborhood_select",
    "invoice",
  ];

  const SUGGESTION_SUPPORTED_TYPES = new Set([
    "text",
    "country_select",
    "province_select",
    "district_select",
    "neighborhood_select",
  ]);
  const MAX_LIST_COLUMNS = 15;
  const LISTABLE_TYPES = new Set([
    "text",
    "number",
    "date",
    "select",
    "employee_select",
    "currency_input",
    "offer_amount",
    "country_select",
    "province_select",
    "district_select",
    "neighborhood_select",
    "project_type_select",
    "project_duration",
    "warning_threshold",
    "budget_input",
    "client_select",
  ]);
  const LOCATION_SELECT_TYPES = new Set<ProjectFormElement["type"]>([
    "country_select",
    "province_select",
    "district_select",
    "neighborhood_select",
  ]);
  const MULTI_MODE_SUPPORTED_TYPES = new Set<ProjectFormElement["type"]>([
    "select",
    "team_member_select",
    "project_type_select",
    "progress_method_select",
    "client_select",
    "employee_select",
  ]);

  const supportsMultipleMode = (element: {
    id: string;
    type: ProjectFormElement["type"];
  }) =>
    MULTI_MODE_SUPPORTED_TYPES.has(element.type) &&
    !LOCATION_SELECT_TYPES.has(element.type);

  const getDefaultAllowMultiple = (element: {
    id: string;
    type: ProjectFormElement["type"];
  }) => {
    if (!supportsMultipleMode(element)) {
      return false;
    }
    // Default behavior: single selection unless explicitly enabled.
    return false;
  };

  const withSelectionModeDefaults = (
    element: ProjectFormElement
  ): ProjectFormElement => {
    if (!supportsMultipleMode(element)) {
      if (element.allowMultiple === undefined || element.allowMultiple === false) {
        return element;
      }
      return { ...element, allowMultiple: false };
    }
    if (typeof element.allowMultiple === "boolean") {
      return element;
    }
    return { ...element, allowMultiple: getDefaultAllowMultiple(element) };
  };

  // Available currency options
  const CURRENCY_OPTIONS = [
    { code: "TRY", symbol: "₺", name: "Türk Lirası" },
    { code: "USD", symbol: "$", name: "Amerikan Doları" },
    { code: "EUR", symbol: "€", name: "Euro" },
    { code: "GBP", symbol: "£", name: "İngiliz Sterlini" },
    { code: "AZN", symbol: "₼", name: "Azerbaycan Manatı" },
    { code: "CHF", symbol: "CHF", name: "İsviçre Frangı" },
    { code: "JPY", symbol: "¥", name: "Japon Yeni" },
    { code: "CNY", symbol: "¥", name: "Çin Yuanı" },
    { code: "RUB", symbol: "₽", name: "Rus Rublesi" },
    { code: "SAR", symbol: "﷼", name: "Suudi Riyali" },
    { code: "AED", symbol: "د.إ", name: "BAE Dirhemi" },
  ];

  // Define non-editable labels (project-specific fields)
  const NON_EDITABLE_LABELS = React.useMemo(
    () => [
      t("projects:projectName"),
      t("projects:clientEmployer"),
      t("projects:startDate"),
      t("projects:endDate"),
      t("projects:projectDescription"),
      t("projects:projectManager"),
    ],
    [t]
  );

  // Create AVAILABLE_PROJECT_FORM_ELEMENTS dynamically with translations
  const AVAILABLE_PROJECT_FORM_ELEMENTS: Omit<ProjectFormElement, "id">[] =
    React.useMemo(
      () => [
        {
          type: "text",
          label: t("projects:clientEmployer"),
          required: false,
          placeholder: t("forms:projectBuilder.enterClientName"),
        },
        {
          type: "employee_select",
          label: t("projects:projectManager"),
          required: false,
          placeholder: t("forms:elementPlaceholders.selectEmployee"),
        },
        {
          type: "invoice",
          label: "Fatura",
          required: false,
        },
        {
          type: "offer_amount",
          label: t("forms:elementLabels.offerAmountInput"),
          required: false,
          placeholder: "0.00",
          currency: "TRY",
        },
        {
          type: "date",
          label: t("projects:startDate"),
          required: false,
          placeholder: "2024-12-31",
        },
        {
          type: "date",
          label: t("projects:endDate"),
          required: false,
          placeholder: "2024-12-31",
        },
        {
          type: "project_duration",
          label: t("projects:projectDurationDays"),
          required: false,
          placeholder: t("forms:projectBuilder.durationInHours"),
        },
        {
          type: "project_type_select",
          label: t("projects:projectType"),
          required: false,
          options: [
            t("projects:projectTypes.webDev"),
            t("projects:projectTypes.mobileApp"),
            t("projects:projectTypes.design"),
            t("projects:projectTypes.consulting"),
            t("projects:projectTypes.infrastructure"),
            t("projects:projectTypes.research"),
          ],
        },
        {
          type: "budget_input",
          label: t("projects:budgetAmount"),
          required: false,
          placeholder: "0.00",
        },
        {
          type: "textarea",
          label: t("projects:projectDescription"),
          required: false,
          placeholder: t("forms:projectBuilder.projectDetailsPlaceholder"),
        },
        {
          type: "country_select",
          label: t("forms:elementLabels.country"),
          required: false,
          options: countryOptions,
        },
        {
          type: "province_select",
          label: "İl",
          required: false,
          placeholder: "İl seçiniz (Sadece Türkiye)",
        },
        {
          type: "district_select",
          label: "İlçe",
          required: false,
          placeholder: "Önce il seçiniz",
        },
        {
          type: "neighborhood_select",
          label: "Mahalle",
          required: false,
          placeholder: "Önce ilçe seçiniz",
        },
        {
          type: "employee_select",
          label: t("forms:elementLabels.employeeSelect"),
          required: false,
          placeholder: t("forms:elementPlaceholders.selectEmployee"),
        },
        {
          type: "currency_input",
          label: t("forms:elementLabels.currencyInput"),
          required: false,
          placeholder: "0.00",
          currency: "TRY", // Default currency
        },
        {
          type: "text",
          label: t("forms:elementLabels.textInput"),
          required: false,
          placeholder: t("forms:elementPlaceholders.customTextField"),
        },
        {
          type: "number",
          label: t("forms:elementLabels.numberInput"),
          required: false,
          placeholder: t("forms:elementPlaceholders.enterNumber"),
        },
        {
          type: "date",
          label: t("forms:elementLabels.datePicker"),
          required: false,
          placeholder: "2024-12-31",
        },
        {
          type: "select",
          label: t("forms:elementLabels.selectList"),
          required: false,
          options: [t("forms:option1"), t("forms:option2")],
        },
        {
          type: "checkbox",
          label: t("forms:elementLabels.checkbox"),
          required: false,
        },
      ],
      [t, countryOptions]
    );
  const visibleAvailableProjectFormElements = React.useMemo(
    () => sanitizePaletteElementsForUser(AVAILABLE_PROJECT_FORM_ELEMENTS),
    [AVAILABLE_PROJECT_FORM_ELEMENTS, sanitizePaletteElementsForUser]
  );

  const [workflowName, setWorkflowName] = useState(
    initialData?.workflowName || initialData?.formName || ""
  );
  const [formName, setFormName] = useState(initialData?.formName || "");
  const [formDescription, setFormDescription] = useState(
    initialData?.formDescription || ""
  );
  const [selectedElements, setSelectedElements] = useState<
    ProjectFormElement[]
  >(() => {
    if (initialData?.elements && initialData?.elementOrder) {
      try {
        const orderArray = JSON.parse(initialData.elementOrder);
        return sanitizeSelectedElementsForUser(
          orderArray
            .map((id: string) =>
              initialData.elements.find((el: any) => el.id === id)
            )
            .filter(Boolean)
            .map((el: ProjectFormElement, idx: number) =>
              withSelectionModeDefaults({
                ...el,
                _key: el._key || `initial-${idx}-${Date.now()}`,
              })
            ) as ProjectFormElement[]
        );
      } catch (e) {
        return sanitizeSelectedElementsForUser(
          initialData.elements.map(
            (el: ProjectFormElement, idx: number) =>
              withSelectionModeDefaults({
                ...el,
                _key: el._key || `initial-${idx}-${Date.now()}`,
              })
          )
        );
      }
    }
    // Default: Always include "Proje Adı" as first element
    return sanitizeSelectedElementsForUser([
      {
        id: "name",
        type: "text",
        label: t("projects:projectName"),
        required: true,
        placeholder: t("projects:enterUniqueProjectName"),
        _key: `default-name-${Date.now()}`, // ✅ Stable key for default element
      },
    ]);
  });
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const listColumnCount = React.useMemo(
    () => selectedElements.filter((el) => el.showInList && el.id !== "name").length,
    [selectedElements]
  );

  const getNextListColumnOrder = (elements: ProjectFormElement[]) => {
    const used = new Set(
      elements
        .filter((el) => el.showInList && el.listColumnOrder)
        .map((el) => el.listColumnOrder)
    );
    for (let i = 1; i <= MAX_LIST_COLUMNS; i++) {
      if (!used.has(i)) return i;
    }
    return MAX_LIST_COLUMNS;
  };

  const normalizeListColumnOrders = useCallback(
    (elements: ProjectFormElement[]) => {
      const ordered = elements
        .filter((el) => el.showInList)
        .sort((a, b) => {
          const aOrder = a.listColumnOrder ?? Number.MAX_SAFE_INTEGER;
          const bOrder = b.listColumnOrder ?? Number.MAX_SAFE_INTEGER;
          if (aOrder !== bOrder) return aOrder - bOrder;
          return 0;
        });
      let order = 1;
      const orderMap = new Map<string, number>();
      ordered.forEach((el) => {
        orderMap.set(el.id, order++);
      });
      return elements.map((el) =>
        el.showInList ? { ...el, listColumnOrder: orderMap.get(el.id) } : el
      );
    },
    []
  );

  const toggleListColumn = useCallback(
    (elementId: string) => {
      setSelectedElements((prev) => {
        const current = prev.find((el) => el.id === elementId);
        if (!current) return prev;

        const enabling = !current.showInList;
        if (enabling) {
          const count = prev.filter((el) => el.showInList).length;
          if (count >= MAX_LIST_COLUMNS) {
            toast.error(
              t("projects:maxListColumns", `En fazla ${MAX_LIST_COLUMNS} kolon seçebilirsiniz`)
            );
            return prev;
          }
          const nextOrder = current.listColumnOrder ?? getNextListColumnOrder(prev);
          return prev.map((el) =>
            el.id === elementId
              ? { ...el, showInList: true, listColumnOrder: nextOrder }
              : el
          );
        }

        const updated = prev.map((el) =>
          el.id === elementId
            ? { ...el, showInList: false, listColumnOrder: undefined }
            : el
        );
        return normalizeListColumnOrders(updated);
      });
    },
    [normalizeListColumnOrders, t]
  );

  const updateListColumnOrder = useCallback(
    (elementId: string, newOrder: number) => {
      setSelectedElements((prev) => {
        const target = prev.find((el) => el.id === elementId);
        if (!target || !target.showInList) return prev;
        const currentOrder = target.listColumnOrder ?? newOrder;

        return prev.map((el) => {
          if (!el.showInList) return el;
          if (el.id === elementId) {
            return { ...el, listColumnOrder: newOrder };
          }
          if (el.listColumnOrder === newOrder) {
            return { ...el, listColumnOrder: currentOrder };
          }
          return el;
        });
      });
    },
    []
  );

  // Helper: Get meaningful ID for form element
  const getElementId = useCallback(
    (element: Omit<ProjectFormElement, "id">): string => {
      // Strategy 1: Use mapping for known elements
      let baseId = ELEMENT_ID_MAP[element.label] || slugify(element.label);

      // Check if ID already exists, if so append counter
      let finalId = baseId;
      let counter = 1;
      while (selectedElements.some((el) => el.id === finalId)) {
        counter++;
        finalId = `${baseId}_${counter}`;
      }

      return finalId;
    },
    [selectedElements]
  );

  const handleSubmit = () => {
    const elementsToPersist = sanitizeSelectedElementsForUser(selectedElements);
    // Validation: Workflow name is required
    if (!workflowName.trim()) {
      toast.error(t("forms:projectBuilder.workflowNameRequired"));
      return;
    }

    // Validation: Proje Adı must exist
    const hasProjectName = elementsToPersist.some(
      (el) => el.label === t("projects:projectName")
    );
    if (!hasProjectName) {
      toast.error(t("forms:projectBuilder.projectNameElementRequired"));
      return;
    }

    if (onSubmit) {
      // Save only template structure
      const templateData = {
        workflowName: workflowName.trim(), // Pass workflow name
        formElements: elementsToPersist,
        elementOrder: JSON.stringify(elementsToPersist.map((el) => el.id)),
        ...(canAssignProjectManagers && projectManagerSelectionTouched
          ? { projectManagerIds: selectedProjectManagerIds }
          : {}),
        // Minimal project data for creation
        name: t("forms:projectBuilder.newWorkflowProject"), // Temporary name, will be filled when workflow starts
        status: WorkflowContainerStatus.PLANNING,
        progress: 0,
        isArchived: false,
      };
      onSubmit(templateData);
    } else {
      // For form building
      handleSave();
    }
  };

  const addElement = useCallback(
    (elementType: Omit<ProjectFormElement, "id">) => {
      if (
        !canViewFinancials &&
        isFinancialProjectElement(elementType as ProjectFormElement)
      ) {
        return;
      }
      const newElementId = getElementId(elementType);
      const newElement: ProjectFormElement = {
        ...elementType,
        id: newElementId, // ??? Use meaningful ID instead of random
        _key: `${Date.now()}-${Math.random()}`, // ??? Stable key for React rendering
        saveToList: (elementType as ProjectFormElement).saveToList || false,
      };
      setSelectedElements((prev) => [
        ...prev,
        withSelectionModeDefaults(newElement),
      ]);
    },
    [canViewFinancials, getElementId, isFinancialProjectElement]
  );

  const removeElement = useCallback((elementId: string) => {
    setSelectedElements((prev) => prev.filter((el) => el.id !== elementId));
  }, []);

  const moveElement = useCallback((fromIndex: number, toIndex: number) => {
    setSelectedElements((prev) => {
      const newElements = [...prev];
      const [movedElement] = newElements.splice(fromIndex, 1);
      newElements.splice(toIndex, 0, movedElement);
      return newElements;
    });
  }, []);

  const updateElement = useCallback(
    (elementId: string, updates: Partial<ProjectFormElement>) => {
      setSelectedElements((prev) => {
        // If label is being updated, also update the ID
        if (updates.label !== undefined) {
          const element = prev.find((el) => el.id === elementId);
          if (element && element.label !== updates.label) {
            // Generate new ID from new label
            let baseId =
              ELEMENT_ID_MAP[updates.label] || slugify(updates.label);

            // Check uniqueness (exclude current element)
            let finalId = baseId;
            let counter = 1;
            while (
              prev.some((el) => el.id === finalId && el.id !== elementId)
            ) {
              counter++;
              finalId = `${baseId}_${counter}`;
            }

            // Add ID to updates
            updates = { ...updates, id: finalId };
          }
        }

        return prev.map((el) =>
          el.id === elementId
            ? withSelectionModeDefaults({ ...el, ...updates })
            : el
        );
      });
    },
    [ELEMENT_ID_MAP]
  );

  const toggleProjectManagerSelection = useCallback((userId: number) => {
    setProjectManagerSelectionTouched(true);
    setSelectedProjectManagerIds((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      }
      return [...prev, userId];
    });
  }, []);

  const handleSave = useCallback(() => {
    const nameToUse = workflowName.trim() || formName.trim();
    const elementsToPersist = sanitizeSelectedElementsForUser(selectedElements);

    if (!nameToUse) {
      toast.error(t("forms:projectBuilder.workflowNameRequired"));
      return;
    }

    if (elementsToPersist.length === 0) {
      toast.error(t("forms:atLeastOneElementRequired"));
      return;
    }

    // Validation: Proje Adı must exist
    const hasProjectName = elementsToPersist.some(
      (el) => el.label === t("projects:projectName")
    );
    if (!hasProjectName) {
      toast.error(t("forms:projectBuilder.projectNameElementRequired"));
      return;
    }

    const formData = {
      id: initialData?.id,
      workflowName: nameToUse,
      formName: nameToUse,
      formDescription: formDescription.trim(),
      elements: elementsToPersist,
      elementOrder: JSON.stringify(elementsToPersist.map((el) => el.id)),
      ...(canAssignProjectManagers && projectManagerSelectionTouched
        ? { projectManagerIds: selectedProjectManagerIds }
        : {}),
      isTemplate: true,
    };

    onSave?.(formData);
  }, [
    workflowName,
    formName,
    formDescription,
    selectedElements,
    sanitizeSelectedElementsForUser,
    selectedProjectManagerIds,
    canAssignProjectManagers,
    projectManagerSelectionTouched,
    initialData?.id,
    onSave,
    t,
  ]);

  const renderFormElement = (element: ProjectFormElement, index: number) => {
    const isProjectName = element.label === t("projects:projectName");
    const isSuggestionEnabled = Boolean(element.saveToList);
    const supportsSuggestions =
      SUGGESTION_SUPPORTED_TYPES.has(element.type) && element.id !== "name";

    // Check if title can be edited: only editable types + not in predefined labels
    const canEditTitle =
      EDITABLE_TYPES.includes(element.type) &&
      !NON_EDITABLE_LABELS.includes(element.label);
    const isListable = LISTABLE_TYPES.has(element.type);
    const isListColumnEligible = isListable && !isProjectName;
    const listToggleDisabled =
      !isListColumnEligible ||
      (!element.showInList && listColumnCount >= MAX_LIST_COLUMNS);
    const supportsSelectionMode = supportsMultipleMode(element);
    const isMultipleModeEnabled = supportsSelectionMode
      ? Boolean(element.allowMultiple)
      : false;

    return (
      <div
        key={element._key || element.id}
        className="bg-white border rounded-lg p-4 mb-4 shadow-sm"
      >
        {!isPreviewMode && !readonly && (
          <div className="flex justify-between items-center mb-3 pb-2 border-b">
            <div className="flex items-center space-x-2">
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded font-medium">
                {element.type.toUpperCase()}
              </span>
              <input
                type="text"
                value={element.label}
                onChange={(e) =>
                  updateElement(element.id, { label: e.target.value })
                }
                className="text-sm font-medium bg-transparent border-none focus:outline-none focus:ring-0 p-0"
                placeholder={t("forms:projectBuilder.elementLabel")}
                disabled={!canEditTitle}
              />
              {isProjectName && (
                <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                  {t("forms:projectBuilder.requiredCannotRemove")}
                </span>
              )}
              <div className="flex items-center space-x-2 ml-2">
                <button
                  type="button"
                  onClick={() => toggleListColumn(element.id)}
                  disabled={listToggleDisabled}
                  title={
                    !isListColumnEligible
                      ? isProjectName
                        ? "Proje adı zaten sabit"
                        : "Bu alan kolonda gösterilemez"
                      : listToggleDisabled
                      ? `En fazla ${MAX_LIST_COLUMNS} kolon seçebilirsiniz`
                      : t("forms:projectBuilder.showInColumn")
                  }
                  className={`flex items-center space-x-2 text-xs font-medium transition-colors ${
                    listToggleDisabled
                      ? "text-gray-300 cursor-not-allowed"
                      : "text-gray-600 hover:text-emerald-700"
                  }`}
                >
                  <span
                    className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${
                      element.showInList
                        ? "bg-emerald-500"
                        : "bg-gray-300"
                    } ${listToggleDisabled ? "opacity-60" : ""}`}
                  >
                    <span
                      className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                        element.showInList ? "translate-x-3.5" : "translate-x-1"
                      }`}
                    />
                  </span>
                  <span>{t("forms:projectBuilder.inColumn")}</span>
                </button>
                {element.showInList && (
                  <select
                    value={element.listColumnOrder || 1}
                    onChange={(e) =>
                      updateListColumnOrder(element.id, Number(e.target.value))
                    }
                    className="text-xs border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    title="Kolon sırası"
                  >
                    {Array.from({ length: MAX_LIST_COLUMNS }, (_, i) => i + 1).map(
                      (order) => (
                        <option key={order} value={order}>
                          {order}
                        </option>
                      )
                    )}
                  </select>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-1">
              {index > 0 && (
                <button
                  onClick={() => moveElement(index, index - 1)}
                  className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                  disabled={isProjectName}
                >
                  <ArrowUpIcon className="h-4 w-4" />
                </button>
              )}
              {index < selectedElements.length - 1 && (
                <button
                  onClick={() => moveElement(index, index + 1)}
                  className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                >
                  <ArrowDownIcon className="h-4 w-4" />
                </button>
              )}
              <button
                onClick={() => removeElement(element.id)}
                disabled={isProjectName}
                className={`p-1 rounded transition-colors ${
                  isProjectName
                    ? "text-gray-300 cursor-not-allowed"
                    : "text-gray-400 hover:text-red-600 hover:bg-red-50"
                }`}
                title={
                  isProjectName
                    ? t("forms:projectBuilder.projectNameCannotRemove")
                    : t("forms:projectBuilder.remove")
                }
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Currency Settings */}
        {!isPreviewMode &&
          !readonly &&
          false &&
          (element.type === "currency_input" ||
            element.type === "offer_amount") && (
          <div className="mb-3 p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-md border border-green-200">
            <label className="block text-xs font-medium text-gray-600 mb-2">
              💰 {t("forms:currencySettings.selectCurrency")}
            </label>
            <select
              value={(element as any).currency || "TRY"}
              onChange={(e) =>
                updateElement(element.id, { currency: e.target.value } as any)
              }
              className="w-full px-3 py-2 text-sm border border-green-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
            >
              {CURRENCY_OPTIONS.map((curr) => (
                <option key={curr.code} value={curr.code}>
                  {curr.symbol} - {curr.name} ({curr.code})
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-2">
              {t("forms:currencySettings.currencyDescription")}
            </p>
          </div>
        )}

        {/* Suggestion Settings */}
        {!isPreviewMode && !readonly && supportsSuggestions && (
          <div className="mb-3 p-3 bg-indigo-50 rounded-md border border-indigo-200">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-gray-800">
                  Cevabı listeye al
                </p>
                <p className="text-xs text-gray-600">
                  Girilen değerler proje türüne özel öneri listesinde tutulur.
                </p>
              </div>
              <button
                type="button"
                onClick={() =>
                  updateElement(element.id, { saveToList: !isSuggestionEnabled })
                }
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  isSuggestionEnabled ? "bg-indigo-600" : "bg-gray-300"
                }`}
                aria-pressed={isSuggestionEnabled}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                    isSuggestionEnabled ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
            {isSuggestionEnabled && workflowContainerId && (
              <button
                type="button"
                onClick={() => handleOpenSuggestionManager(element)}
                className="mt-2 text-xs font-medium text-indigo-700 hover:text-indigo-800"
              >
                Kayıtlı değerleri yönet
              </button>
            )}
          </div>
        )}

        {!isPreviewMode && !readonly && supportsSelectionMode && (
          <div className="mb-3 p-3 bg-slate-50 rounded-md border border-slate-200">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-gray-800">
                  {t("forms:projectBuilder.selectionMode")}
                </p>
                <p className="text-xs text-gray-600">
                  {t(
                    "forms:projectBuilder.selectionModeDescription",
                    "Bu alanda tekli veya çoklu seçim yapılmasını belirleyin."
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`text-xs font-semibold ${
                    !isMultipleModeEnabled ? "text-blue-700" : "text-gray-500"
                  }`}
                >
                  {t("forms:projectBuilder.singleSelection", "Tekli seçim")}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    updateElement(element.id, {
                      allowMultiple: !isMultipleModeEnabled,
                    })
                  }
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    isMultipleModeEnabled ? "bg-blue-600" : "bg-gray-300"
                  }`}
                  aria-pressed={isMultipleModeEnabled}
                  title={
                    isMultipleModeEnabled
                      ? t("forms:projectBuilder.multipleSelection", "Çoklu seçim")
                      : t("forms:projectBuilder.singleSelection", "Tekli seçim")
                  }
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                      isMultipleModeEnabled ? "translate-x-5" : "translate-x-0"
                    }`}
                  />
                </button>
                <span
                  className={`text-xs font-semibold ${
                    isMultipleModeEnabled ? "text-blue-700" : "text-gray-500"
                  }`}
                >
                  {t("forms:projectBuilder.multipleSelection", "Çoklu seçim")}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Element Settings */}
        {!isPreviewMode &&
          !readonly &&
          (element.type === "select" ||
            element.type === "project_type_select" ||
            element.type === "team_member_select") && (
            <div className="mb-3 p-3 bg-gray-50 rounded-md space-y-3">
              <label className="block text-xs font-medium text-gray-600 mb-2">
                {t("forms:options")}
              </label>

              {/* Seçenek Listesi */}
              <div className="space-y-2">
                {(element.options || []).map((option, index) => (
                  <div key={index} className="flex items-center gap-2">
                    {/* Input Field */}
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => {
                        const newOptions = [...(element.options || [])];
                        newOptions[index] = e.target.value;
                        updateElement(element.id, { options: newOptions });
                      }}
                      placeholder={`${t("forms:option1")} ${index + 1}`}
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />

                    {/* Sil Butonu */}
                    <button
                      type="button"
                      onClick={() => {
                        const newOptions = (element.options || []).filter(
                          (_, i) => i !== index
                        );
                        // En az 1 seçenek olmalı
                        if (newOptions.length === 0) {
                          toast.error(t("forms:atLeastOneOptionRequired"));
                          return;
                        }
                        updateElement(element.id, { options: newOptions });
                      }}
                      disabled={(element.options || []).length <= 1}
                      className="p-2 text-gray-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      title={t("forms:removeOption")}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Seçenek Ekle Butonu */}
              <button
                type="button"
                onClick={() => {
                  const newOptions = [
                    ...(element.options || []),
                    `${t("forms:option1")} ${
                      (element.options || []).length + 1
                    }`,
                  ];
                  updateElement(element.id, { options: newOptions });
                }}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-blue-600 bg-white border border-blue-300 rounded-md hover:bg-blue-50 transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                {t("forms:addOption")}
              </button>
            </div>
          )}

        {/* Form Element Template Display */}
        {renderProjectFormField(element)}
      </div>
    );
  };

  const handleOpenSuggestionManager = async (element: ProjectFormElement) => {
    if (!workflowContainerId) {
      return;
    }

    setActiveSuggestionElement(element);
    setShowSuggestionsModal(true);
    setLoadingSuggestions(true);
    try {
      const suggestions = await apiService.getWorkflowContainerValueSuggestions(
        workflowContainerId,
        element.id
      );
      setSuggestionsByElement((prev) => ({
        ...prev,
        [element.id]: suggestions,
      }));
    } catch (error) {
      toast.error("Kayıtlı değerler yüklenemedi.");
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleDeleteSuggestion = async (
    elementId: string,
    suggestionId: number
  ) => {
    if (!workflowContainerId) {
      return;
    }

    try {
      await apiService.deleteWorkflowContainerValueSuggestion(
        workflowContainerId,
        suggestionId
      );
      setSuggestionsByElement((prev) => ({
        ...prev,
        [elementId]: (prev[elementId] || []).filter(
          (item) => item.id !== suggestionId
        ),
      }));
    } catch (error) {
      toast.error("Kayıtlı değer silinemedi.");
    }
  };

  const renderProjectFormField = (element: ProjectFormElement) => {
    // Template mode: Just show the structure, no data entry
    switch (element.type) {
      case "text":
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {element.label}
            </label>
            <div className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-md bg-gray-50">
              <div className="flex items-center justify-center text-gray-500 italic text-sm py-1">
                <DocumentTextIcon className="w-4 h-4 mr-2" />
                {t("forms:projectBuilder.fieldWillBeFilledOnProjectCreation")}
              </div>
            </div>
          </div>
        );

      case "budget_input":
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {element.label}
            </label>
            <div className="relative">
              <div className="w-full px-3 py-2 pl-8 border-2 border-dashed border-gray-300 rounded-md bg-gray-50">
                <div className="flex items-center justify-center text-gray-500 italic text-sm py-1">
                  <DocumentTextIcon className="w-4 h-4 mr-2" />
                  {t(
                    "forms:projectBuilder.fieldWillBeFilledOnProjectCreationBudget"
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case "date_range":
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {element.label}
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  {t("projects:startDate")}
                </label>
                <div className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-md bg-gray-50">
                  <div className="flex items-center justify-center text-gray-500 italic text-xs py-1">
                    <DocumentTextIcon className="w-3 h-3 mr-1" />
                    {t(
                      "forms:projectBuilder.fieldWillBeFilledOnProjectCreationDate"
                    )}
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  {t("projects:endDate")}
                </label>
                <div className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-md bg-gray-50">
                  <div className="flex items-center justify-center text-gray-500 italic text-xs py-1">
                    <DocumentTextIcon className="w-3 h-3 mr-1" />
                    {t(
                      "forms:projectBuilder.fieldWillBeFilledOnProjectCreationDate"
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case "progress_bar":
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {element.label}
            </label>
            <div className="space-y-2">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-gray-400 h-2 rounded-full"
                  style={{ width: "0%" }}
                ></div>
              </div>
              <div className="flex items-center justify-center text-gray-500 italic text-xs py-1">
                <DocumentTextIcon className="w-3 h-3 mr-1" />
                {t(
                  "forms:projectBuilder.fieldWillBeFilledOnProjectCreationNumber"
                )}
              </div>
            </div>
          </div>
        );

      case "client_select":
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {element.label}
            </label>
            <div className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-md bg-gray-50">
              <div className="flex items-center justify-center text-gray-500 italic text-sm py-1">
                <DocumentTextIcon className="w-4 h-4 mr-2" />
                {t(
                  "forms:projectBuilder.fieldWillBeFilledOnProjectCreationSelect"
                )}
              </div>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {t("forms:projectBuilder.canAddNewClient")}
            </p>
          </div>
        );

      case "progress_method_select":
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {element.label}
            </label>
            <div className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-md bg-gray-50">
              <div className="flex items-center justify-center text-gray-500 italic text-sm py-1">
                <DocumentTextIcon className="w-4 h-4 mr-2" />
                {t(
                  "forms:projectBuilder.fieldWillBeFilledOnProjectCreationSelect"
                )}
              </div>
              {element.options && element.options.length > 0 && (
                <div className="mt-2 text-xs text-gray-400 text-center">
                  {t("forms:options")}: {element.options.slice(0, 2).join(", ")}
                  {element.options.length > 2 && "..."}
                </div>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {t("forms:projectBuilder.selectionAffectsWarning")}
            </p>
          </div>
        );

      case "invoice":
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {element.label}
            </label>
            <div className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-md bg-gray-50">
              <div className="flex items-center justify-center text-gray-500 italic text-sm py-1">
                <DocumentTextIcon className="w-4 h-4 mr-2" />
                Fatura secimi ve dosya yukleme
              </div>
            </div>
          </div>
        );

      case "project_duration":
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {element.label}
            </label>
            <div className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-md bg-gray-50">
              <div className="flex items-center justify-center text-gray-500 italic text-sm py-1">
                <DocumentTextIcon className="w-4 h-4 mr-2" />
                {t(
                  "forms:projectBuilder.fieldWillBeFilledOnProjectCreationNumber"
                )}
              </div>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {t("forms:projectBuilder.autoCalculated")}
            </p>
          </div>
        );

      case "date":
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {element.label}
            </label>
            <div className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-md bg-gray-50">
              <div className="flex items-center justify-center text-gray-500 italic text-sm py-1">
                <DocumentTextIcon className="w-4 h-4 mr-2" />
                {t(
                  "forms:projectBuilder.fieldWillBeFilledOnProjectCreationDate"
                )}
              </div>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {t("forms:projectBuilder.exampleDateFormat")}
            </p>
          </div>
        );

      case "textarea":
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {element.label}
            </label>
            <div className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-md bg-gray-50 min-h-[80px]">
              <div className="flex items-center justify-center text-gray-500 italic text-sm py-8">
                <DocumentTextIcon className="w-4 h-4 mr-2" />
                {t(
                  "forms:projectBuilder.fieldWillBeFilledOnProjectCreationMultiline"
                )}
              </div>
            </div>
          </div>
        );

      case "number":
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {element.label}
            </label>
            <div className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-md bg-gray-50">
              <div className="flex items-center justify-center text-gray-500 italic text-sm py-1">
                <DocumentTextIcon className="w-4 h-4 mr-2" />
                {t(
                  "forms:projectBuilder.fieldWillBeFilledOnProjectCreationNumber"
                )}
              </div>
            </div>
          </div>
        );

      case "select":
      case "project_type_select":
      case "country_select":
      case "province_select":
      case "district_select":
      case "neighborhood_select":
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {element.label}
            </label>
            <div className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-md bg-gray-50">
              <div className="flex items-center justify-center text-gray-500 italic text-sm py-1">
                <DocumentTextIcon className="w-4 h-4 mr-2" />
                {t(
                  "forms:projectBuilder.fieldWillBeFilledOnProjectCreationSelect"
                )}
              </div>
              {element.options && element.options.length > 0 && (
                <div className="mt-2 text-xs text-gray-400 text-center">
                  {t("forms:options")}: {element.options.slice(0, 3).join(", ")}
                  {element.options.length > 3 && "..."}
                </div>
              )}
            </div>
          </div>
        );

      case "employee_select":
        if (isPreviewMode) {
          return (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {element.label}
              </label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                <option value="">
                  {t("forms:elementPlaceholders.selectEmployee")}
                </option>
                {employees.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.firstName} {employee.lastName}
                  </option>
                ))}
              </select>
            </div>
          );
        }
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {element.label}
            </label>
            <div className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-md bg-gray-50">
              <div className="flex items-center justify-center text-gray-500 italic text-sm py-1">
                <DocumentTextIcon className="w-4 h-4 mr-2" />
                {t(
                  "forms:projectBuilder.fieldWillBeFilledOnProjectCreationSelect"
                )}
              </div>
              {employees.length > 0 && (
                <div className="mt-2 text-xs text-gray-400 text-center">
                  {t("forms:options")}:{" "}
                  {employees
                    .slice(0, 3)
                    .map((e) => `${e.firstName} ${e.lastName}`)
                    .join(", ")}
                  {employees.length > 3 && "..."}
                </div>
              )}
            </div>
          </div>
        );

      case "checkbox":
        return (
          <div>
            <div className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-md bg-gray-50">
              <div className="flex items-center justify-center text-gray-500 italic text-sm py-1">
                <DocumentTextIcon className="w-4 h-4 mr-2" />
                {t(
                  "forms:projectBuilder.fieldWillBeFilledOnProjectCreationCheckbox"
                )}
              </div>
              <div className="mt-1 text-xs text-gray-400 text-center">
                {element.label}
              </div>
            </div>
          </div>
        );

      case "currency_input":
      case "offer_amount":
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {element.label}
            </label>
            <div className="relative">
              <div className="w-full px-3 py-2 border-2 border-dashed border-green-300 rounded-md bg-gradient-to-r from-green-50 to-emerald-50">
                <div className="flex items-center text-gray-500 italic text-sm py-1">
                  <DocumentTextIcon className="w-4 h-4 mr-2" />
                  {t("forms:projectBuilder.amountWillUseProjectCurrency")}
                </div>
              </div>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {t("forms:projectBuilder.currencyWillBeSelectedOnStart")}
            </p>
          </div>
        );

      default:
        // Diğer standart form elementleri için fallback
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {element.label}
            </label>
            <div className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-md bg-gray-50">
              <div className="flex items-center justify-center text-gray-500 italic text-sm py-1">
                <DocumentTextIcon className="w-4 h-4 mr-2" />
                {t("forms:projectBuilder.fieldWillBeFilledOnProjectCreation")}
              </div>
            </div>
          </div>
        );
    }
  };

  const activeSuggestions = activeSuggestionElement
    ? suggestionsByElement[activeSuggestionElement.id] || []
    : [];

  const filteredProjectManagers = React.useMemo(() => {
    if (!projectManagerSearch.trim()) {
      return projectManagers;
    }
    const term = projectManagerSearch.trim().toLowerCase();
    return projectManagers.filter((user) => {
      const fullName = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
      const haystack = [
        fullName,
        user.username,
        user.email,
        user.firstName,
        user.lastName,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [projectManagers, projectManagerSearch]);

  return (
    <div className="h-full flex flex-col">
      {canAssignProjectManagers && !readonly && (
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setCurrentStep(1)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                currentStep === 1
                  ? "bg-blue-50 border-blue-200 text-blue-700"
                  : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
            >
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-white text-xs">
                1
              </span>
              {t("forms:projectBuilder.projectFormElements")}
            </button>
            <button
              type="button"
              onClick={() => setCurrentStep(2)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                currentStep === 2
                  ? "bg-indigo-50 border-indigo-200 text-indigo-700"
                  : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
            >
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-indigo-600 text-white text-xs">
                2
              </span>
              {t("projects:projectManagers")}
            </button>
            <div className="text-xs text-gray-500 ml-auto">
              {t("projects:projectManagerSelectionDescription")}
            </div>
          </div>
        </div>
      )}

      {/* Preview Mode Toggle */}
      {(currentStep === 1 || !canAssignProjectManagers || readonly) && (
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-gray-700">
                {t("forms:projectBuilder.formMode")}
              </span>
              {!readonly && (
                <button
                  onClick={() => setIsPreviewMode(!isPreviewMode)}
                  className={`flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    isPreviewMode
                      ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {isPreviewMode ? (
                    <PencilIcon className="h-4 w-4 mr-2" />
                  ) : (
                    <EyeIcon className="h-4 w-4 mr-2" />
                  )}
                  {isPreviewMode
                    ? t("forms:projectBuilder.edit")
                    : t("forms:projectBuilder.preview")}
                </button>
              )}
            </div>
            <div className="text-sm text-gray-500 flex items-center gap-2">
              <span>
                {selectedElements.length} {t("forms:projectBuilder.elementsAdded")}
              </span>
              <span className="text-gray-300">|</span>
              <span>
                {t("projects:listColumns", "Kolonlar")}: {listColumnCount}/
                {MAX_LIST_COLUMNS}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {(!canAssignProjectManagers || currentStep === 1 || readonly) && (
          <div className="flex h-full">
            {/* Element Palette */}
            {!isPreviewMode && !readonly && (
              <div className="w-80 bg-white border-r border-gray-200 p-4 overflow-y-auto">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {t("forms:projectBuilder.projectFormElements")}
                </h3>
                <div className="space-y-2">
                  {visibleAvailableProjectFormElements.map((element, index) => (
                    <button
                      key={index}
                      onClick={() => addElement(element)}
                      className="w-full text-left p-3 bg-gray-50 hover:bg-blue-50 rounded-lg transition-colors border border-gray-200 hover:border-blue-300 group"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-gray-800 group-hover:text-blue-800">
                            {element.label}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {element.type.toUpperCase()}
                          </div>
                        </div>
                        <PlusIcon className="h-5 w-5 text-gray-400 group-hover:text-blue-600" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Form Builder Area */}
            <div className={isPreviewMode || readonly ? "w-full" : "flex-1"}>
              <div className="p-6">
                {selectedElements.length === 0 ? (
                  <div className="text-center py-16 text-gray-500">
                    <div className="text-6xl mb-4">+</div>
                    <h3 className="text-xl font-medium mb-2">
                      {t("forms:projectBuilder.startCreatingProjectForm")}
                    </h3>
                    <p className="text-sm">
                      {readonly
                        ? t("forms:projectBuilder.noElementsInForm")
                        : t("forms:projectBuilder.addElementsFromLeft")}
                    </p>
                  </div>
                ) : (
                  <div className="max-w-2xl mx-auto">
                    {/* Workflow Name Input - ALWAYS at the top */}
                    {!readonly && (
                      <div className="mb-6 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl shadow-sm border border-blue-100">
                        <label className="block text-lg font-bold text-gray-900 mb-3">
                          {workflowNameLabel}
                          <span className="text-red-500 ml-1">*</span>
                        </label>
                        <input
                          type="text"
                          value={workflowName}
                          onChange={(e) => setWorkflowName(e.target.value)}
                          placeholder={t("workflows:workflowNamePlaceholder")}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder-gray-400"
                          required
                        />
                        <p className="text-xs text-gray-600 mt-2">
                          {t("forms:projectBuilder.workflowNameDescription")}
                        </p>
                      </div>
                    )}

                    <div className="space-y-4">
                      {selectedElements.map((element, index) =>
                        renderFormElement(element, index)
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {canAssignProjectManagers && currentStep === 2 && !readonly && (
          <div className="p-6">
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200">
                <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-indigo-50 via-blue-50 to-sky-50">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">
                        {t("projects:projectManagers")}
                      </h3>
                      <p className="text-sm text-slate-600 mt-1">
                        {t("projects:projectManagerSelectionDescription")}
                      </p>
                    </div>
                    <span className="text-xs font-medium text-indigo-700 bg-indigo-100 px-2 py-1 rounded-full">
                      {t("common:selected")}:{" "}
                      {selectedProjectManagerIds.length}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-5">
                    <div className="relative flex-1">
                      <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <input
                        type="text"
                        value={projectManagerSearch}
                        onChange={(e) => setProjectManagerSearch(e.target.value)}
                        placeholder={t("common:searchPlaceholder")}
                        className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setProjectManagerSelectionTouched(true);
                          setSelectedProjectManagerIds(
                            filteredProjectManagers.map((user) => user.id)
                          );
                        }}
                        className="px-3 py-2 text-xs font-medium border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50"
                      >
                        {t("common:selectAll")}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setProjectManagerSelectionTouched(true);
                          setSelectedProjectManagerIds([]);
                        }}
                        className="px-3 py-2 text-xs font-medium border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50"
                      >
                        {t("common:clear")}
                      </button>
                    </div>
                  </div>

                  {!projectManagersLoaded ? (
                    <div className="text-sm text-slate-500">
                      {t("common:loading")}
                    </div>
                  ) : filteredProjectManagers.length === 0 ? (
                    <div className="text-sm text-slate-500">
                      {t("projects:noProjectManagersFound")}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {filteredProjectManagers.map((user) => {
                        const isSelected =
                          selectedProjectManagerIds.includes(user.id);
                        const displayName =
                          user.firstName && user.lastName
                            ? `${user.firstName} ${user.lastName}`
                            : user.username;
                        return (
                          <button
                            key={user.id}
                            type="button"
                            onClick={() => toggleProjectManagerSelection(user.id)}
                            className={`flex items-center gap-3 border rounded-xl px-4 py-3 text-left transition-colors ${
                              isSelected
                                ? "border-blue-300 bg-blue-50"
                                : "border-slate-200 hover:bg-slate-50"
                            }`}
                          >
                            <div className="relative">
                              <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
                                <UserCircleIcon className="h-6 w-6" />
                              </div>
                              {isSelected && (
                                <CheckCircleIcon className="h-4 w-4 text-blue-600 absolute -right-1 -bottom-1 bg-white rounded-full" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold text-slate-900 truncate">
                                {displayName}
                              </div>
                              <div className="text-xs text-slate-500 truncate">
                                @{user.username}
                                {user.email ? ` - ${user.email}` : ""}
                              </div>
                            </div>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              readOnly
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleProjectManagerSelection(user.id);
                              }}
                              className="h-4 w-4 text-blue-600 border-slate-300 rounded"
                            />
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Footer */}
      {!readonly && (
        <div className="p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {selectedElements.length > 0 && !isPreviewMode && (
                <span className="flex items-center text-amber-600">
                  <span className="w-2 h-2 bg-amber-400 rounded-full mr-2"></span>
                  {t("forms:projectBuilder.formBeingEdited")}
                </span>
              )}
              {isPreviewMode && (
                <span className="flex items-center text-blue-600">
                  <span className="w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
                  {t("forms:projectBuilder.previewMode")}
                </span>
              )}
            </div>
            <div className="flex space-x-3">
              {onCancel && (
                <button
                  onClick={onCancel}
                  className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                >
                  {t("common:cancel")}
                </button>
              )}
              <button
                onClick={handleSubmit}
                disabled={selectedElements.length === 0}
                className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <span>🚀</span>
                <span>
                  {onSubmit
                    ? t("forms:projectBuilder.createWorkflow")
                    : isEditMode
                    ? t("forms:projectBuilder.update")
                    : t("common:save")}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuggestionsModal && activeSuggestionElement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
            <div className="p-5 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {t("forms:projectBuilder.savedValues")}
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  {activeSuggestionElement.label}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowSuggestionsModal(false);
                  setActiveSuggestionElement(null);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5">
              {loadingSuggestions ? (
                <div className="text-sm text-gray-500">Yükleniyor...</div>
              ) : activeSuggestions.length === 0 ? (
                <div className="text-sm text-gray-500">
                  Bu alan için kayıtlı değer yok.
                </div>
              ) : (
                <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
                  {activeSuggestions.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between gap-2 border border-gray-200 rounded-md px-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className="text-sm text-gray-900 truncate">
                          {item.value}
                        </p>
                        {item.usageCount !== undefined && (
                          <p className="text-[11px] text-gray-500">
                            {item.usageCount} kez kullanıldı
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          handleDeleteSuggestion(
                            activeSuggestionElement.id,
                            item.id
                          )
                        }
                        className="text-xs text-red-600 hover:text-red-700"
                      >
                        Sil
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
